import { useState } from "react";
import { Send } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Reveal, SectionHead } from "./shared";

const inputCls =
  "w-full rounded-lg border border-gold/25 bg-lake-deep/60 px-4 py-3 text-sm text-parchment-ivory placeholder:text-parchment-ivory/35 transition-colors focus:border-gold-shimmer focus:outline-none";

const TIPI = [
  { value: "generale", label: "Messaggio generale" },
  { value: "stampa", label: "Stampa e redazioni" },
  { value: "blogger", label: "Bookstagram / BookTok / blog letterari" },
  { value: "arc", label: "Richiesta copia ARC (lettura anticipata)" },
  { value: "eventi", label: "Eventi, presentazioni e librerie" },
];

const NewsletterPanel = () => {
  const [form, setForm] = useState({ nome: "", email: "" });
  const [formato, setFormato] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/newsletter", {
        email: form.email,
        nome: form.nome || undefined,
        formato: formato || undefined,
      });
      toast.success(res.data.message);
      setForm({ nome: "", email: "" });
      setFormato(null);
    } catch {
      toast.error("Qualcosa è andato storto. Riprova tra poco.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card-parchment rounded-xl p-8 sm:p-10" data-testid="newsletter-panel">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Il Diario di Bordo</p>
      <h3 className="mt-3 font-serif text-2xl text-ink-dark sm:text-3xl">
        Il primo capitolo, prima di tutti
      </h3>
      <p className="mt-4 text-sm leading-relaxed text-ink-muted">
        Iscriviti alla newsletter: riceverai in anteprima l'estratto del primo capitolo, la data
        del cover reveal, le leggende del lago raccontate una a una e l'invito all'evento di
        lancio. Niente spam, solo inchiostro e acqua di lago.
      </p>
      <form onSubmit={submit} className="mt-7 space-y-3" data-testid="newsletter-form">
        <input
          data-testid="newsletter-name-input"
          type="text"
          value={form.nome}
          onChange={(e) => setForm({ ...form, nome: e.target.value })}
          placeholder="Il tuo nome (facoltativo)"
          className="w-full rounded-lg border border-ink-dark/15 bg-white/60 px-4 py-3 text-sm text-ink-dark placeholder:text-ink-muted/60 transition-colors focus:border-gold focus:outline-none"
        />
        <input
          data-testid="newsletter-email-input"
          type="email"
          required
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="La tua email"
          className="w-full rounded-lg border border-ink-dark/15 bg-white/60 px-4 py-3 text-sm text-ink-dark placeholder:text-ink-muted/60 transition-colors focus:border-gold focus:outline-none"
        />
        <div data-testid="newsletter-formato-group">
          <p className="text-xs font-semibold text-ink-dark">
            E dopo il cartaceo? <span className="font-normal text-ink-muted">(facoltativo)</span>
          </p>
          <div className="mt-2 flex gap-2">
            {["ebook", "podcast"].map((f) => (
              <button
                key={f}
                type="button"
                data-testid={`newsletter-formato-${f}`}
                onClick={() => setFormato(formato === f ? null : f)}
                className={`flex-1 rounded-full border px-4 py-2.5 text-sm font-semibold capitalize transition-colors duration-300 ${
                  formato === f
                    ? "border-lake-deep bg-lake-deep text-parchment-ivory"
                    : "border-ink-dark/15 bg-white/60 text-ink-muted hover:border-ink-dark/35"
                }`}
              >
                {f === "ebook" ? "Ebook" : "Podcast"}
              </button>
            ))}
          </div>
        </div>
        <button
          data-testid="newsletter-submit-button"
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-lake-deep px-6 py-3.5 text-sm font-semibold text-parchment-ivory transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {loading ? "Iscrizione in corso…" : "Iscriviti al Diario di Bordo"}
          <Send className="h-4 w-4" />
        </button>
        <p className="text-[11px] leading-relaxed text-ink-muted">
          Iscrivendoti accetti di ricevere email sul romanzo e sulla saga. Potrai cancellarti in
          qualsiasi momento.
        </p>
      </form>
    </div>
  );
};

const ContactPanel = () => {
  const [form, setForm] = useState({ nome: "", email: "", tipo: "generale", messaggio: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/contact", form);
      toast.success(res.data.message);
      setForm({ nome: "", email: "", tipo: "generale", messaggio: "" });
    } catch {
      toast.error("Invio non riuscito. Controlla i campi e riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl border border-gold/25 bg-lake-teal/30 p-8 sm:p-10" data-testid="contact-panel">
      <p className="text-sm leading-relaxed text-parchment-ivory/60">
        Stampa, bookstagrammer, booktoker, librerie, biblioteche, pro loco ed eventi: questo è il
        canale giusto.
      </p>
      <form onSubmit={submit} className="mt-7 space-y-4" data-testid="contact-form">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <input
            data-testid="contact-name-input"
            type="text"
            required
            minLength={2}
            value={form.nome}
            onChange={(e) => setForm({ ...form, nome: e.target.value })}
            placeholder="Nome e cognome"
            className={inputCls}
          />
          <input
            data-testid="contact-email-input"
            type="email"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            placeholder="La tua email"
            className={inputCls}
          />
        </div>
        <select
          data-testid="contact-type-select"
          value={form.tipo}
          onChange={(e) => setForm({ ...form, tipo: e.target.value })}
          className={`${inputCls} appearance-none`}
        >
          {TIPI.map((t) => (
            <option key={t.value} value={t.value} className="bg-lake-deep text-parchment-ivory">
              {t.label}
            </option>
          ))}
        </select>
        <textarea
          data-testid="contact-message-input"
          required
          minLength={10}
          rows={5}
          value={form.messaggio}
          onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
          placeholder="Il tuo messaggio"
          className={`${inputCls} resize-none`}
        />
        <button
          data-testid="contact-form-submit-button"
          type="submit"
          disabled={loading}
          className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gold px-6 py-3.5 text-sm font-semibold text-ink-dark transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 sm:w-auto"
        >
          {loading ? "Invio in corso…" : "Invia la richiesta"}
          <Send className="h-4 w-4" />
        </button>
      </form>
    </div>
  );
};

export default function Contatti() {
  return (
    <>
      <section id="contatti" className="relative bg-lake-teal/20 py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            kicker="Contatti"
            title="Scrivici"
            sub="Presentazioni, copie ARC, materiali stampa, eventi sul territorio: raccontaci cosa hai in mente."
          />
          <Reveal className="mx-auto mt-14 max-w-2xl">
            <ContactPanel />
          </Reveal>
        </div>
      </section>

      <section id="newsletter" className="relative bg-lake-deep py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <SectionHead
            kicker="Contatti & Newsletter"
            title="Resta sulla riva del lago"
            sub="L'uscita si avvicina: chi è iscritto al Diario di Bordo saprà tutto per primo."
          />
          <Reveal className="mx-auto mt-14 max-w-2xl">
            <NewsletterPanel />
          </Reveal>
        </div>
      </section>
    </>
  );
}
